"use client";

import React, { useMemo } from "react";
import { useAdminUsers } from "@/lib/hooks/api/admin";
import type { AppUser } from "@/lib/types/database";
import { SmartCompanyDisplay } from "@/components/shared/smart-display/SmartCompanyDisplay";
import { SmartContactDisplay } from "@/components/shared/smart-display/SmartContactDisplay";
import type { SimpleFieldConfig } from "../../../types/index";
import { getFilterDisplayValue } from "../utils/filter-display-helpers";
import {
  isCompanyFilterField,
  isContactFilterField,
  isUserFilterField,
} from "../utils/field-kind";

interface FilterChipValueProps {
  fieldConfig: SimpleFieldConfig;
  filter: { type: string; value: any };
}

/** Coerce a single-or-array filter value into a clean list of scalar ids. */
function toIdList(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : [value];
  return raw
    .filter((v) => v !== null && v !== undefined && v !== "")
    .map((v) => String(v));
}

function userDisplayName(user: Pick<AppUser, "first_name" | "last_name" | "email">): string {
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  return name || user.email || "";
}

/**
 * Resolve `app_users.id` values to human names via the shared admin-users cache
 * (same source SmartUserDisplay uses). Renders "Unknown user" for an id that is
 * not in the cache — never the raw UUID.
 */
function UserFilterValue({ value }: { value: unknown }) {
  const { data: users } = useAdminUsers();

  const text = useMemo(() => {
    const nameById = new Map<string, string>();
    (users ?? []).forEach((u: AppUser) => {
      if (u?.id) nameById.set(u.id, userDisplayName(u));
    });
    const ids = toIdList(value);
    if (ids.length === 0) return "";
    return ids.map((id) => nameById.get(id) || "Unknown user").join(", ");
  }, [users, value]);

  if (!text) return null;
  return (
    <span className="max-w-[180px] truncate font-medium" title={text}>
      {text}
    </span>
  );
}

/** Company-reference filter value → resolved company name(s). */
function CompanyFilterValue({ value }: { value: unknown }) {
  const ids = toIdList(value);
  if (ids.length === 0) return null;
  return (
    <span className="inline-flex max-w-[180px] items-center gap-1 truncate">
      {ids.map((id) => (
        <SmartCompanyDisplay
          key={id}
          companyId={id}
          showIcon={false}
          showPopover={false}
          fallbackText="Unknown company"
          className="!px-0 text-xs font-medium"
        />
      ))}
    </span>
  );
}

/** Contact ("client") filter value → resolved contact name(s). */
function ContactFilterValue({ value }: { value: unknown }) {
  const ids = toIdList(value);
  if (ids.length === 0) return null;
  return (
    <span className="inline-flex max-w-[180px] items-center gap-1 truncate">
      {ids.map((id) => (
        <SmartContactDisplay
          key={id}
          contactId={id}
          showIcon={false}
          showPopover={false}
          fallbackText="Unknown contact"
          className="!px-0 text-xs font-medium"
        />
      ))}
    </span>
  );
}

/**
 * Renders the human-readable value for an active-filter chip.
 *
 * Entity-reference fields (user / company / contact) resolve their scalar id(s)
 * to names so a chip never shows a raw UUID (e.g. "Created By" showed the
 * `created_by` app_users UUID). Everything else falls back to the plain,
 * option-aware text formatter.
 */
export function FilterChipValue({ fieldConfig, filter }: FilterChipValueProps) {
  if (isUserFilterField(fieldConfig)) {
    return <UserFilterValue value={filter.value} />;
  }
  if (isCompanyFilterField(fieldConfig)) {
    return <CompanyFilterValue value={filter.value} />;
  }
  if (isContactFilterField(fieldConfig)) {
    return <ContactFilterValue value={filter.value} />;
  }

  const displayValue = getFilterDisplayValue(filter, fieldConfig);
  if (!displayValue) return null;
  return (
    <span className="max-w-[180px] truncate font-medium" title={displayValue}>
      {displayValue}
    </span>
  );
}
